// File: app/src/components/workout/SessionCompleteModal.js
//
// Full-screen celebratory overlay shown when a guided session finishes.
// Displays confetti, session stats, and collects optional feedback before saving.

import React, { useMemo, useState } from "react";
import { Activity, Trophy, Clock, Dumbbell, Flame } from "lucide-react";
import Card from "../common/Card";
import ConfettiCelebration from "../common/ConfettiCelebration";
import WorkoutFeedback from "./WorkoutFeedback";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import usePlanEditor from "../../hooks/usePlanEditor";
import ShareWorkoutDialog from "../social/ShareWorkoutDialog";
import RepsDoneList from "./RepsDoneList";
import RepsAdjustSheet from "./RepsAdjustSheet";
import { useAuth } from "../../contexts/AuthContext";
import { getWorkoutSettings } from "../../hooks/useWorkoutSettings";
import { Users, Globe } from "lucide-react";
import { motion } from "framer-motion";
import { ChipGrid } from "../profile/ProfileParts";
import { ACTIVITY_TYPES } from "../../utils/loggedSession";
import { isFreeTraining } from "../../utils/freeTraining";
import {
  checklistItems,
  checklistLeftEntries,
  checklistProgress,
  guessActivityType,
  logToPlan,
  loggedCount,
} from "../../utils/freeTrainingLog";
import { NotLoggedList } from "./FreeTrainingChecklist";

// Walk the plan tree and return an array of leaf exercises
const flattenExercises = (node) => {
  if (!node) return [];
  if (!node.children || node.children.length === 0) {
    return [
      {
        label: node.label,
        value: node.value,
        unit: node.unit,
        type: node.type,
      },
    ];
  }
  return node.children.flatMap((child) => flattenExercises(child));
};

const SessionCompleteModal = ({
  workoutPlan,
  // Optional: when supplied, the session the plan ran with becomes editable
  // here, before it is persisted as the session record (#76, widened by #77
  // from loads to every field).
  onPlanChange,
  elapsedSessionTime,
  // Optional: the reps sets done (doneRepsSets) and the correction that
  // writes one (#144). Without onAmendReps the list isn't shown.
  repsSets,
  onAmendReps,
  onSave,
  onSkip,
  saveLabel = "Save and finish",
  skipLabel = "Skip",
  // Carry on in Free training, in this same session (#199). Left out where
  // there's nothing to carry on in.
  onKeepGoing = null,
  // Free training: what was logged as you went (#157), the checklist jotted
  // before (#164), your exercise list for its numbers, and the log's setter
  // for adding what was done but not logged.
  freeLog = [],
  freeChecklist = null,
  exerciseList = [],
  onFreeLogChange,
  // The machines' calories, from telemetry/energy sessionMachineEnergy:
  // { kcal, source: "machine" | "power" }, or null with no machine.
  machineEnergy = null,
}) => {
  const { user } = useAuth();
  // Free training has no plan to say what it was, so the user does (#146),
  // starting from what the log suggests (#157). Optional: cleared, it saves
  // as "Free training".
  const free = isFreeTraining(workoutPlan);
  const [activityType, setActivityType] = useState(() =>
    free ? guessActivityType(freeLog) : null,
  );
  const guessedType = useMemo(() => (free ? guessActivityType(freeLog) : null), [free, freeLog]);
  // The log as a plan tree, a draft in the planner's own editor: reorder,
  // group, edit, delete or add before it's saved (#157).
  const [freePlan, setFreePlan] = useState(() => (free ? logToPlan(freeLog) : null));
  // Checklist items not logged (#164). One added here joins the log and the
  // draft plan, and stays listed as Added.
  const [addedIds, setAddedIds] = useState([]);
  const notLogged = useMemo(
    () =>
      free
        ? checklistProgress(checklistItems(freeChecklist), freeLog).filter(
            (r) => r.left > 0 || addedIds.includes(r.item.id),
          )
        : [],
    [free, freeChecklist, freeLog, addedIds],
  );
  const waiting = notLogged.filter((r) => !addedIds.includes(r.item.id)).length;
  const addFromChecklist = (row) => {
    const entries = checklistLeftEntries(row, exerciseList, Math.round(elapsedSessionTime || 0));
    if (!entries.length || !onFreeLogChange) return;
    onFreeLogChange((log) => [...log, ...entries]);
    // Converted after the log so far: a piece logged by distance alone takes
    // the time since the last entry, as it would have when logged.
    const nodes = logToPlan([...freeLog, ...entries]).children.slice(-entries.length);
    setFreePlan((plan) =>
      plan ? { ...plan, children: [...(plan.children || []), ...nodes] } : { ...logToPlan(entries), children: nodes },
    );
    setAddedIds((ids) => [...ids, row.item.id]);
  };
  // Read once per mount: the setting can't change while this overlay is up,
  // and the server applies the same value when the session is saved.
  const [autoShare] = useState(
    () => getWorkoutSettings().shareCompletedWorkouts ?? "friends",
  );

  // `onPlanChange` is CoachView's `setWorkoutPlan` — a React state setter, so
  // it takes the updater functions usePlanEditor hands it. The no-op fallback
  // keeps the hook unconditional when the modal is rendered read-only.
  const { planDisplayProps } = usePlanEditor(
    free ? setFreePlan : onPlanChange || (() => {}),
  );

  // The confetti is the "victory" — it fires only when the user keeps the
  // session (Save), never on Discard. Paired with the deferred fanfare in
  // useWorkoutCoach so a cancelled session celebrates nothing (issue #41).
  const [celebrating, setCelebrating] = useState(false);
  const [repsEdit, setRepsEdit] = useState(null);

  const stats = useMemo(() => {
    const exercises = flattenExercises(workoutPlan);
    const workExercises = exercises.filter((e) => {
      const t = (e.type || "").toLowerCase();
      return t !== "rest" && t !== "setrest";
    });
    const elapsedSec = elapsedSessionTime || 0;
    const elapsedMin = Math.round(elapsedSec / 60);
    const elapsedDisplay =
      elapsedMin >= 60
        ? `${Math.floor(elapsedMin / 60)}h ${elapsedMin % 60}m`
        : `${elapsedMin} min`;

    // A bike or rower's own count when one was paired, so this matches its
    // console. Otherwise a rough guess of 6.5 kcal a minute.
    const fromMachine = machineEnergy?.kcal > 0;
    const calories = fromMachine ? Math.round(machineEnergy.kcal) : Math.round(elapsedMin * 6.5);
    const caloriesApprox = !fromMachine || machineEnergy.source !== "machine";

    return {
      exerciseCount: workExercises.length,
      elapsedDisplay,
      calories,
      caloriesApprox,
    };
  }, [workoutPlan, elapsedSessionTime, machineEnergy]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <ConfettiCelebration trigger={celebrating} />

      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md rounded-2xl border border-border/50 bg-background p-6 card-glow overflow-y-auto max-h-[90vh]"
      >
        {/* Trophy icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 12 }}
          className="flex justify-center mb-4"
        >
          <div className="h-20 w-20 rounded-full bg-primary/20 flex items-center justify-center ring-4 ring-primary/10">
            <Trophy className="h-10 w-10 text-primary" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="text-center mb-5"
        >
          <h2 className="text-2xl font-bold mb-1">Workout Complete!</h2>
          <p className="text-muted-foreground">
            Great job. You've earned your rest.
          </p>
        </motion.div>

        {/* Stats grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="grid grid-cols-3 gap-3 mb-5"
        >
          <Card className="bg-surface p-3 text-center">
            <Clock className="h-4 w-4 text-primary mx-auto mb-1" />
            <p className="text-lg font-bold">{stats.elapsedDisplay}</p>
            <p className="text-xs text-muted-foreground">Duration</p>
          </Card>
          {free ? (
            <Card className="bg-surface p-3 text-center">
              <Dumbbell className="h-4 w-4 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold">{loggedCount(freeLog)}</p>
              <p className="text-xs text-muted-foreground">Logged</p>
            </Card>
          ) : (
            <Card className="bg-surface p-3 text-center">
              <Dumbbell className="h-4 w-4 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold">{stats.exerciseCount}</p>
              <p className="text-xs text-muted-foreground">Exercises</p>
            </Card>
          )}
          <Card className="bg-surface p-3 text-center">
            <Flame className="h-4 w-4 text-orange-500 mx-auto mb-1" />
            <p className="text-lg font-bold">{stats.caloriesApprox ? "~" : ""}{stats.calories}</p>
            <p className="text-xs text-muted-foreground">Calories</p>
          </Card>
        </motion.div>

        {free && (
          <div className="mb-5 text-left">
            <p className="mb-2 flex items-baseline justify-between gap-2 text-sm font-medium">
              What did you do?
              {guessedType && (
                <span className="text-xs font-normal text-muted-foreground">from your log</span>
              )}
            </p>
            <ChipGrid
              options={ACTIVITY_TYPES}
              value={activityType}
              onChange={setActivityType}
              allowNone
            />
            {notLogged.length > 0 && onFreeLogChange && (
              <div className="mt-4">
                <p className="mb-1 text-sm font-medium">
                  {waiting
                    ? `${waiting} ${waiting === 1 ? "item" : "items"} on your checklist not logged`
                    : "Added from your checklist"}
                </p>
                <p className="mb-2 text-xs text-muted-foreground">
                  Did any and forget to log them? Add them here. The rest are left off.
                </p>
                <NotLoggedList rows={notLogged} added={addedIds} list={exerciseList} onAdd={addFromChecklist} />
              </div>
            )}
            {freePlan?.children?.length > 0 && (
              <div className="mt-4">
                <p className="mb-1 text-sm font-medium">What you logged</p>
                <p className="mb-2 text-xs text-muted-foreground">
                  Saved as your session. Drag to reorder, or tap a step to fix it.
                </p>
                <SessionPlanDisplay plan={freePlan} editable {...planDisplayProps} />
              </div>
            )}
          </div>
        )}

        {/* Workout name */}
        {!free && workoutPlan?.label && (
          <p className="text-sm text-muted-foreground text-center mb-4">
            {workoutPlan.label}
          </p>
        )}

        {onAmendReps && !free && <RepsDoneList sets={repsSets} onEdit={setRepsEdit} />}
        <RepsAdjustSheet
          open={!!repsEdit}
          onOpenChange={(open) => {
            if (!open) setRepsEdit(null);
          }}
          set={repsEdit}
          onSubmit={onAmendReps || (() => {})}
        />

        {/* What actually happened — the last chance to correct the session
            before this plan becomes the saved record. Was load-only; #77
            widened it to the planner's own editor so a phase's time and reps
            are as correctable as its weight.

            The full editor, reordering and grouping included: nothing here has
            been written yet — this plan BECOMES the record on save — so it is
            still a draft, and a session that ran out of the order it was
            written in should be correctable before it's filed. */}
        {onPlanChange && !free && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="mb-4 text-left"
          >
            <SessionPlanDisplay
              plan={workoutPlan}
              editable
              {...planDisplayProps}
            />
          </motion.div>
        )}

        {/* Sharing. A saved workout posts itself now (issue #79), so this
            says so before you save rather than after — a workout appearing in
            your mates' feeds should never be something you find out about.
            The manual share dialog stays for those who post nothing
            automatically; offering both would double-post. */}
        {user && (
          <div className="mb-4">
            {autoShare === "off" ? (
              <ShareWorkoutDialog planData={free ? freePlan : workoutPlan} label="Post this workout" className="w-full" />
            ) : (
              <div className="flex items-start gap-2 text-xs text-muted-foreground text-left">
                {autoShare === "public" ? (
                  <Globe className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                ) : (
                  <Users className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                )}
                <span>
                  Saving posts this workout to{" "}
                  {autoShare === "public" ? "everyone" : "your mates"}. You can
                  remove it from the feed, or change who sees your training,
                  under Sharing on the Social tab.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Feedback + Save/Skip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          <WorkoutFeedback
            onSave={(feedback) => {
              setCelebrating(true);
              onSave(free ? { ...feedback, activityType, freePlan } : feedback);
            }}
            onSkip={() => onSkip()}
            saveLabel={saveLabel}
            skipLabel={skipLabel}
            alsoAction={
              onKeepGoing && !free
                ? {
                    label: "Keep going",
                    hint: "Adds Free training to this session",
                    icon: Activity,
                    onClick: () => onKeepGoing(),
                  }
                : null
            }
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SessionCompleteModal;
